import React, { useContext, useEffect, Suspense } from "react";
import dynamic from 'next/dynamic';
import Layout from "@/shared/layout/Layout";
import css from "./home.module.scss";
import { withPageWrapper } from "@/services/utils/withPageWrapper";
import PageWrapper from "@/shared/layout/page/PageWrapper";
import HomeHeader from "./components/Header/HomeHeader";
import HomeCreationGuide from "./components/CreationGuide/CreationGuide";
import UserContext from "../../context/user/UserContext";
import PagesDataService from "@/services/domain/PagesDataService";
import PublicationsService from "@/services/domain/PublicationsService";
import ProductsService from "@/services/domain/ProductsService";
import AuthService from "@/services/domain/AuthService";
import OpenGraphHead from "@/shared/OpenGraphHead/OpenGraphHead";
import { serverSideTranslations } from "next-i18next/pages/serverSideTranslations";
import { COMMON_NAMESPACES } from "../../i18n";
import LazyLoadWrapper from "@/components/common/LazyLoadWrapper";
import { ProductSortCriteria } from "@/models/Product";

const HomePerks = dynamic(() => import("@/pages/home/components/Perks/HomePerks"), { ssr: false });
const HowTo = dynamic(() => import("@/components/HowTo/HowTo"), { ssr: false });
const HomeGifts = dynamic(() => import("@/pages/home/components/HomeGifts/HomeGifts"), { ssr: false });
const HomeBlog = dynamic(() => import("@/pages/home/components/Blog/HomeBlog"), { ssr: false });
const HomeThemes = dynamic(() => import("@/pages/home/components/Themes"), { ssr: false });
const HomeFAQ = dynamic(() => import("@/pages/home/components/HomeFAQ"), { ssr: false });
const namespacesRequired = [
  ...COMMON_NAMESPACES,
  "home",
  "pot",
  "concept",
  "publications",
];

const Home = ({ t, principal, pagesData, topPublications, gifts }) => {
  const userContext = useContext(UserContext);
  const { activeList, isFetching } = userContext;

  return (
    <Layout principal={principal} homePage={true}>
      <PageWrapper customClass={css.noPadding}>
        <OpenGraphHead
          title={t("home:meta-title")}
          description={t("home:meta-description")}
        />

        <HomeHeader
          activeList={activeList}
          isFetching={isFetching}
          pagesData={pagesData}
        />

        <HomeCreationGuide />
        <LazyLoadWrapper className={css.skeleton} fallbackHeight={400} >
          <HomePerks />
        </LazyLoadWrapper>

        <LazyLoadWrapper className={css.skeleton} fallbackHeight={200}>
          <HowTo activeList={activeList} />
        </LazyLoadWrapper>

        <LazyLoadWrapper className={css.skeleton} fallbackHeight={500}>
          <HomeGifts gifts={gifts?.products} />
        </LazyLoadWrapper>

        <LazyLoadWrapper className={css.skeleton} fallbackHeight={300}>
          <HomeBlog publications={topPublications} />
        </LazyLoadWrapper>

        <LazyLoadWrapper className={css.skeleton} fallbackHeight={200}>
          <HomeThemes />
        </LazyLoadWrapper>

        <LazyLoadWrapper className={css.skeleton} fallbackHeight={300}>
          <HomeFAQ />
        </LazyLoadWrapper>
      </PageWrapper>
    </Layout>
  );
};

export async function getServerSideProps(context) {
  const { locale ,query } = context;

  // Get authenticated user principal
  const principal = AuthService.getPrincipal(context);
  const defaultProductsSearchCriteria = {
    sort_criteria: query.sort || ProductSortCriteria.PLACEMENT,
    is_favorite: query.favorite || 0,
    categories: query.categories || undefined,
    sub_categories: query.sub_categories || undefined,
    brands: query.brands || undefined,
    age_ranges: query.age && query.age !== "0" ? query.age : undefined,
    min_price:
      query.min_price && parseInt(query.min_price) > DEFAULT_MIN_PRICE
        ? query.min_price * 100
        : undefined,
    max_price:
      query.max_price && parseInt(query.max_price) < DEFAULT_MAX_PRICE
        ? query.max_price * 100
        : undefined,
    gender: query.gender ? query.gender : undefined,
    is_avdn: query.is_avdn ? query.is_avdn : 0,
    inspiration_list: query.inspiration_list || undefined,
  };
  const [pagesData, topPublications, gifts, translations] = await Promise.all([
    PagesDataService.get(context),
    PublicationsService.getTop(4, context).catch((error) => {
      console.log(
        'Error while retrieving top four publications for home page:',
        error.stack || error
      );
      return null;
    }),
    ProductsService.find({...defaultProductsSearchCriteria , is_avdn : 0}, 15, 0, context),
    serverSideTranslations(locale, namespacesRequired),
  ]);
  return {
    props: {
      principal,
      pagesData,
      topPublications,
      gifts,
      ...translations,
    },
  };
}
export default withPageWrapper({ namespacesRequired: namespacesRequired })(
  Home
);
